/**
 * Shared pieces of missions 21-32 (10 §2): places that lie outside any compound - a hide among the rocks,
 * a wreck, a rendezvous - kept clear of the garrison and the fieldworks the budget buys afterwards;
 * sentries who walk round a building; and the last-minute flagging of a marker for the strip map.
 */

import { atan2, cos, PI, sin, TAU } from '../../sim/math';
import type { EnemyKind, MissionDef, ObjectiveRule, PropDef, PropKind, Rect, SpawnDef } from '../../sim/types';
import { NO_BUILD, NO_STAND } from '../builder';
import { FLAG } from '../grid';
import type { Point } from '../types';
import type { GoalCtx } from './types';

export const FIRST_CAPTIVE_ID = 700;
export const RECALL: ObjectiveRule = { type: 'recall', mode: 'normal' };

/** A hide's rocks: big enough to hide a prone man, low enough that a standing one sees and shoots over them. */
const ROCK = { scale: 1.3, radius: 1.3, ring: 4.9 } as const;
/** Half the side of the square a man lies in, inside the ring. */
export const HIDE_HALF = 2.5;

/** Everything north of the belt's wire line: the goal band and the belt, which no linear obstacle divides. */
export function openNorth(g: GoalCtx): Rect {
  const belt = g.ctx.bands[g.ctx.bands.length - 2];
  const z1 = (belt?.kind === 'belt' ? belt.z1 : g.rect.z1 + 20) - 7;
  return { x0: 5, z0: 5, x1: g.ctx.sizeX - 5, z1 };
}

/** +1 when the pickup (the quiet side) lies east of the compound, -1 when west. */
export const quietDir = (g: GoalCtx): 1 | -1 => (g.ctx.plan.roadEast ? -1 : 1);

export const squareAt = (x: number, z: number, half: number): Rect => ({ x0: x - half, z0: z - half, x1: x + half, z1: z + half });

const dist = (a: Point, b: Point): number => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));

export interface ClearingOptions {
  within: Rect;
  /** Radius of open ground wanted: nothing solid, wet, dug, mined, reserved or walked by a patrol. */
  radius: number;
  /** Keep this far from each of these (the pickup, the other sites); the chosen point is added. */
  minDist?: number;
  from?: Point[];
  /** Prefer points near here (within `maxDist`) while any can be found. */
  near?: Point;
  maxDist?: number;
  /** Stay this far outside a rectangle (the compound's wire, for a place that overlooks it). */
  outside?: { rect: Rect; pad: number };
  /** Take the best of many candidates - the one farthest from `from` - instead of the first that will do. */
  spread?: boolean;
  /** Best of many by a measure of the caller's own (higher is better); overrides `spread`'s. */
  score?: (p: Point) => number;
  /** Anything else the place must satisfy (within sight of what it overlooks, say). */
  accept?: (p: Point) => boolean;
  /** Places to stay away from by their own distances (the pickup, where the Osprey has to come down). */
  clearOf?: ReadonlyArray<{ at: Point; radius: number }>;
  /** Roads are ground too: a roadside site wants one close, most others want none under them. */
  avoid?: number;
}

/**
 * A point with `radius` of clear ground round it, or null. Deterministic in the goal's stream. With `spread`
 * (or a `score`) it is the best of many: the clear point farthest from everything in `from`, or best by the
 * caller's measure - always at least `minDist` from the rest - which finds room for sites that must lie well
 * apart where plain rejection sampling gives up.
 */
export function clearing(g: GoalCtx, o: ClearingOptions): Point | null {
  const avoid = o.avoid ?? (NO_BUILD | FLAG.path);
  const r = o.within;
  const pad = o.radius + 1;
  if (r.x1 - r.x0 < pad * 2 || r.z1 - r.z0 < pad * 2) return null;
  let best: Point | null = null;
  let bestD = -Infinity;
  let bestApart = Infinity;
  let found = 0;
  const many = o.spread === true || o.score !== undefined;
  for (let tries = 0; tries < 320 && found < (many ? 60 : 1); tries++) {
    const p = { x: Math.floor(g.rng.range(r.x0 + pad, r.x1 - pad)) + 0.5, z: Math.floor(g.rng.range(r.z0 + pad, r.z1 - pad)) + 0.5 };
    // The first tries honour `near`; after that anywhere in the rectangle will do.
    if (o.near && o.maxDist !== undefined && tries < 80 && dist(p, o.near) > o.maxDist) continue;
    if (o.clearOf?.some((c) => dist(p, c.at) < c.radius)) continue;
    if (o.outside && within(o.outside.rect, p, o.outside.pad)) continue;
    if (o.accept && !o.accept(p)) continue;
    const apart = Math.min(Infinity, ...(o.from ?? []).map((q) => dist(p, q)));
    // `minDist` is a hard floor whichever way the best is chosen.
    if (apart < (o.minDist ?? 0)) continue;
    if (g.map.mask.hitsCircle(p.x, p.z, o.radius, avoid)) continue;
    found++;
    const worth = o.score ? o.score(p) : apart;
    if (worth > bestD) {
      best = p;
      bestD = worth;
      bestApart = apart;
    }
  }
  if (!best || bestApart < (o.minDist ?? 0)) return null;
  o.from?.push(best);
  return best;
}

/** As `clearing`, but the mission cannot exist without it. */
export function requiredClearing(g: GoalCtx, what: string, o: ClearingOptions): Point {
  const p = clearing(g, o);
  if (p) return p;
  g.map.problems.push(`${g.ctx.spec.id}: no clear ground for ${what}`);
  return { x: (o.within.x0 + o.within.x1) / 2, z: (o.within.z0 + o.within.z1) / 2 };
}

/** Reserve open ground: no scatter, no mines, no wire run, and nobody the budget buys is posted on it. */
export function keepClear(g: GoalCtx, p: Point, radius: number): void {
  g.map.mask.circle(p.x, p.z, radius, FLAG.keep);
}

/**
 * A hide: a horseshoe of boulders with its back to `threat`, open on the far side. A man lying in the
 * middle is out of sight of anyone beyond the rocks (the sim's line of sight to a 0.4 m target); standing,
 * he sees over them. Wants `hideRadius` of clear ground. Returns the square to lie in.
 */
export const hideRadius = ROCK.ring + ROCK.radius + 0.6;

export function rockHide(g: GoalCtx, at: Point, threat: Point): Rect {
  const toward = atan2(threat.x - at.x, threat.z - at.z);
  // Touching boulders: the chord between neighbours is one diameter and a hair, so none overlaps the next.
  const chord = ROCK.radius * 2 + 0.03;
  // asin by its series (the argument is about a quarter): the purity rules keep Math.asin out of the generator.
  const half = chord / (2 * ROCK.ring);
  const step = 2 * (half + (half * half * half) / 6 + (3 * half * half * half * half * half) / 40);
  const each = Math.floor((PI * 0.72) / step);
  for (let i = -each; i <= each; i++) {
    const a = toward + i * step;
    g.map.add('rock', at.x + sin(a) * ROCK.ring, at.z + cos(a) * ROCK.ring, { scale: ROCK.scale, yaw: ((i + each) * 1.7) % TAU });
  }
  keepClear(g, at, ROCK.ring - ROCK.radius);
  // The way in, on the side away from the threat.
  const back = { x: at.x - sin(toward) * (ROCK.ring + 1), z: at.z - cos(toward) * (ROCK.ring + 1) };
  g.map.mask.line([[at.x, at.z], [back.x, back.z]], 1.2, FLAG.keep);
  return squareAt(at.x, at.z, HIDE_HALF);
}

/** A free cell for a man to stand on near a point, or null; never on reserved ground unless `onKeep`. */
export function standNear(g: GoalCtx, x: number, z: number, reach = 5, onKeep = false): [number, number] | null {
  return g.map.nearestOpen(x, z, reach, onKeep ? NO_STAND : NO_STAND | FLAG.keep);
}

/**
 * Sentries who walk round a building, `count` of them spaced round the loop: the gap between their sweeps is
 * the way in for a man who will not be seen (as the hangar of mission 9). The loop stays `inside` (the compound,
 * unless told otherwise). False when there was no room.
 */
export function sweepRound(g: GoalCtx, prop: PropDef, count: number, kind: EnemyKind, off = 4, inside: Rect = g.rect): boolean {
  const b = g.map.bounds(prop.kind, prop.x, prop.z, prop);
  const corners = [[b.x0 - off, b.z1 + off], [b.x1 + off, b.z1 + off], [b.x1 + off, b.z0 - off], [b.x0 - off, b.z0 - off]] as const;
  const loop: Array<[number, number]> = [];
  for (const [x, z] of corners) {
    // Stay inside the perimeter however close to it the building stands.
    const cx = Math.max(inside.x0 + 2.5, Math.min(inside.x1 - 2.5, x));
    const cz = Math.max(inside.z0 + 2.5, Math.min(inside.z1 - 2.5, z));
    const spot = g.map.nearestOpen(cx, cz, 5, NO_STAND);
    if (spot) loop.push(spot);
  }
  if (loop.length < 4) return false;
  for (let n = 0; n < count; n++) {
    const start = Math.floor((n * 4) / count);
    const nodes = loop.map((_, i) => loop[(i + start) % 4] ?? ([0, 0] as [number, number]));
    const [a, next] = nodes;
    if (!a || !next) continue;
    g.map.spawn({ kind, x: a[0], z: a[1], facing: atan2(next[0] - a[0], next[1] - a[1]), patrol: nodes });
    for (const [x, z] of nodes) g.map.mask.circle(x, z, 1.5, FLAG.keep);
  }
  g.map.mask.line([...loop, loop[0] ?? [0, 0]], 1.5, FLAG.path);
  return true;
}

/** Put clutter of one kind round a point outside the compound; whatever fits, fits. Returns how many did. */
export function strew(g: GoalCtx, kind: PropKind, count: number, at: Point, from: number, to: number): number {
  let placed = 0;
  for (let tries = 0; tries < count * 12 && placed < count; tries++) {
    const a = g.rng.range(0, TAU);
    const d = g.rng.range(from, to);
    const avoid = FLAG.solid | FLAG.road | FLAG.trench | FLAG.water | FLAG.mine | FLAG.path;
    if (g.map.place(kind, at.x + sin(a) * d, at.z + cos(a) * d, { margin: 0.5, avoid })) placed++;
  }
  return placed;
}

/**
 * Markers are flagged `target` (the strip map draws flagged props) only once the map is finished: a prop
 * flagged while the garrison is still being bought gets a sentry posted on it (posts.ts), and nobody
 * guards a rendezvous he does not know about.
 */
export function flagTargets(def: MissionDef, ids: readonly number[]): void {
  for (const p of def.props) if (ids.includes(p.id)) p.target = true;
}

/** Spawns of a finished mission that a timed rule will wake. */
export const wokenBy = (def: MissionDef, tag: string): SpawnDef[] =>
  def.spawns.filter((s) => s.dormantUntil === 'timedEvent' && s.wakeTag === tag);

/** Is this point inside the rectangle grown by `pad`? */
export const within = (r: Rect, p: Point, pad = 0): boolean =>
  p.x >= r.x0 - pad && p.x <= r.x1 + pad && p.z >= r.z0 - pad && p.z <= r.z1 + pad;
